import { useEffect, useMemo, useState } from "react";
import { ChevronsUpDown, Check, Download, MoreHorizontal, RefreshCw, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useUsers,
  DEFAULT_USER_FILTERS,
  type UserFilters,
  type PlatformUser,
} from "@/hooks/useUserFilters";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import UserProfileSheet from "@/components/users/UserProfileSheet";


type ActionKind = "impersonate" | "suspend" | "reactivate" | "deactivate" | "grant_admin" | "remove_admin";

interface ActionTarget {
  kind: ActionKind;
  user: PlatformUser;
}

const RoleBadge = ({ role }: { role: string | null | undefined }) => {
  const r = (role ?? "").toLowerCase();
  if (r === "superuser") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
        Superuser
      </span>
    );
  }
  if (r === "admin") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">
        Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/40 text-slate-300 border border-slate-600/40">
      Member
    </span>
  );
};

const StatusBadge = ({ status }: { status: string | null | undefined }) => {
  const s = (status ?? "").toLowerCase();
  if (s === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Active
      </span>
    );
  }
  if (s === "suspended") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
        Suspended
      </span>
    );
  }
  if (s === "pending") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">
        Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/40 text-slate-400 border border-slate-600/40">
      Deactivated
    </span>
  );
};

const fullName = (u: PlatformUser): string => {
  const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return name || u.email || "Unnamed user";
};

const isProfileComplete = (u: PlatformUser): boolean => {
  return Boolean((u.first_name ?? "").trim() && (u.last_name ?? "").trim());
};

const extractError = (e: unknown): string => {
  if (e && typeof e === "object") {
    if ("message" in e && typeof (e as { message?: unknown }).message === "string") {
      return (e as { message: string }).message;
    }
  }
  return "Action failed";
};

const csvEscape = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadUsersCsv = (rows: PlatformUser[]) => {
  const headers = ['first_name', 'last_name', 'email', 'company', 'role', 'status', 'profile_completed', 'phone', 'position', 'joined_at'];
  const lines = [headers.join(',')];
  for (const u of rows) {
    lines.push([
      u.first_name ?? '',
      u.last_name ?? '',
      u.email ?? '',
      u.company_name ?? '',
      u.admin_role ?? '',
      u.status ?? '',
      u.profile_completed ? 'Yes' : 'No',
      u.personal_phone_number ?? '',
      u.company_position ?? '',
      u.joined_at ?? '',
    ].map(csvEscape).join(','));
  }
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

interface ComboboxFilterProps {
  value: string;
  onValueChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  width?: string;
}

function ComboboxFilter({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  width = "w-[240px]",
}: ComboboxFilterProps) {
  const [open, setOpen] = useState(false);
  const selectedLabel = value
    ? options.find((o) => o.value === value)?.label ?? value
    : placeholder;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${width} h-9 flex items-center justify-between rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 hover:bg-slate-800 focus:outline-none`}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-slate-800 border-slate-700" style={{ width: "260px" }}>
        <Command className="bg-slate-800">
          <CommandInput
            placeholder={searchPlaceholder}
            className="text-slate-100 placeholder:text-slate-500 border-slate-700"
          />
          <CommandList>
            <CommandEmpty className="text-slate-400 text-sm py-3 text-center">{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => {
                    onValueChange(o.value);
                    setOpen(false);
                  }}
                  className="text-slate-200 data-[selected=true]:bg-slate-700 cursor-pointer"
                >
                  <Check className={`mr-2 h-4 w-4 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type FilterType = "role" | "company" | "profileCompleted" | "suspendedByCompany" | "joinedDate";

const AVAILABLE_FILTERS = [
  { value: "role", label: "Role" },
  { value: "company", label: "Company" },
  { value: "profileCompleted", label: "Profile complete" },
  { value: "suspendedByCompany", label: "Suspended by company" },
  { value: "joinedDate", label: "Date joined" },
];

export default function Users() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<UserFilters>(() => ({
    ...DEFAULT_USER_FILTERS,
    status: searchParams.get("status") ?? null,
    roles: searchParams.get("roles")?.split(",").filter(Boolean) ?? [],
    companyIds: searchParams.get("companyIds")?.split(",").filter(Boolean) ?? [],
    profileCompleted:
      searchParams.get("profileCompleted") === "true"
        ? true
        : searchParams.get("profileCompleted") === "false"
        ? false
        : null,
    suspendedByCompany:
      searchParams.get("suspendedByCompany") === "true"
        ? true
        : searchParams.get("suspendedByCompany") === "false"
        ? false
        : null,
    joinedDateRanges: (() => {
      const raw = searchParams.get("joinedDateRanges");
      if (!raw) return [];
      try { return JSON.parse(raw); } catch { return []; }
    })(),
    page: parseInt(searchParams.get("page") ?? "1", 10) || 1,
    pageSize: 50,
  }));

  useEffect(() => {
    const params: Record<string, string> = {};
    if (filters.status) params.status = filters.status;
    if (filters.roles.length) params.roles = filters.roles.join(",");
    if (filters.companyIds.length) params.companyIds = filters.companyIds.join(",");
    if (filters.profileCompleted !== null) params.profileCompleted = String(filters.profileCompleted);
    if (filters.suspendedByCompany !== null) params.suspendedByCompany = String(filters.suspendedByCompany);
    if (filters.joinedDateRanges.length) params.joinedDateRanges = JSON.stringify(filters.joinedDateRanges);
    if (filters.page > 1) params.page = String(filters.page);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateFilters = (updates: Partial<UserFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...updates,
      page: "page" in updates ? updates.page ?? 1 : 1,
    }));
  };

  const clearFilters = () => setFilters(DEFAULT_USER_FILTERS);

  const { users, total, totalPages, isLoading, isFetching, error, refetch } = useUsers(filters);

  useEffect(() => {
    const channel = supabase
      .channel('admin-users-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: ['user-counts'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const { data: userCounts } = useQuery({
    queryKey: ['user-counts'],
    staleTime: 30_000,
    queryFn: async () => {
      const { data: adminIds } = await supabase
        .from('admin_users')
        .select('id');
      const excludeIds = new Set((adminIds ?? []).map(a => a.id));
      const { data, error } = await supabase
        .from('profiles')
        .select('id, status');
      if (error) throw error;
      const rows = (data ?? []).filter(r => !excludeIds.has(r.id));
      return {
        all: rows.length,
        active: rows.filter(r => r.status === 'active').length,
        suspended: rows.filter(r => (r.status as string) === 'suspended').length,
        deactivated: rows.filter(r => (r.status as string) === 'deactivated').length,
      };
    },
  });

  const { data: allCompanies = [] } = useQuery({
    queryKey: ["filter-companies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name")
        .neq("id", "2c24dd37-7434-4b8a-919e-2a54fb7aae20")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

  const [search, setSearch] = useState("");
  const [activeFilterType, setActiveFilterType] = useState<FilterType | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = `${fullName(u)} ${u.email ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, search]);

  const [target, setTarget] = useState<ActionTarget | null>(null);
  const [reason, setReason] = useState("");
  const [suspensionType, setSuspensionType] = useState<'temporary' | 'permanent'>('temporary');
  const [suspensionDays, setSuspensionDays] = useState<string>('7');
  const [submitting, setSubmitting] = useState(false);
  const [profileTarget, setProfileTarget] = useState<PlatformUser | null>(null);

  const closeDialog = () => {
    if (submitting) return;
    setTarget(null);
    setReason("");
  };

  const openAction = (kind: ActionKind, user: PlatformUser) => {
    setTarget({ kind, user });
    setReason("");
    setSuspensionType('temporary');
    setSuspensionDays('7');
  };

  const handleConfirm = async () => {
    if (!target) return;
    const { kind, user } = target;
    const trimmed = reason.trim();

    if ((kind === "suspend" || kind === "deactivate") && !trimmed) {
      toast.error("A reason is required");
      return;
    }

    setSubmitting(true);
    try {
      if (kind === "impersonate") {
        const newTab = window.open("", "_blank");
        const { error: rpcErr } = await supabase.rpc("admin_impersonate_user", {
          p_user_id: user.id,
        });
        if (rpcErr) {
          if (newTab) newTab.close();
          throw rpcErr;
        }
        if (newTab) {
          newTab.location.href = "https://app.doyencegroup.com";
        } else {
          window.open("https://app.doyencegroup.com", "_blank");
        }
        setTarget(null);
        return;
      }

      if (kind === "suspend") {
        if (!user.company_id) throw new Error("User has no company");
        let suspendedUntil: string | null = null;
        if (suspensionType === 'temporary') {
          const days = parseInt(suspensionDays, 10);
          if (!days || days < 1 || days > 365) throw new Error('Please enter a valid number of days (1-365)');
          const endDate = new Date();
          endDate.setDate(endDate.getDate() + days);
          suspendedUntil = endDate.toISOString();
        }
        const { error: rpcErr } = await supabase.rpc("suspend_member", {
          p_user_id: user.id,
          p_company_id: user.company_id,
          p_reason: trimmed,
          p_suspended_until: suspendedUntil,
        });
        if (rpcErr) throw rpcErr;
        toast.success("User suspended");
      } else if (kind === "reactivate") {
        if (!user.company_id) throw new Error("User has no company");
        const { error: rpcErr } = await supabase.rpc("reactivate_company_member", {
          p_user_id: user.id,
          p_company_id: user.company_id,
        });
        if (rpcErr) throw rpcErr;
        toast.success("User reactivated");
      } else if (kind === "deactivate") {
        if (!user.company_id) throw new Error("User has no company");
        const { error: rpcErr } = await supabase.rpc("deactivate_user_from_company", {
          p_user_id: user.id,
          p_company_id: user.company_id,
          p_reason: trimmed,
        });
        if (rpcErr) throw rpcErr;
        toast.success("User deactivated");
      } else if (kind === "grant_admin") {
        if (!user.company_id) throw new Error("User has no company");
        const { error: rpcErr } = await supabase.rpc("grant_admin_role", {
          p_user_id: user.id,
          p_company_id: user.company_id,
        });
        if (rpcErr) throw rpcErr;
        toast.success("Admin role granted");
      } else if (kind === "remove_admin") {
        if (!user.company_id) throw new Error("User has no company");
        const { error: rpcErr } = await supabase.rpc("remove_admin_role", {
          p_user_id: user.id,
          p_company_id: user.company_id,
        });
        if (rpcErr) throw rpcErr;
        toast.success("Admin role removed");
      }

      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      queryClient.invalidateQueries({ queryKey: ['user-counts'] });
      setTarget(null);
      setReason("");
    } catch (e) {
      const raw = extractError(e);
      const msg =
        raw.toLowerCase().includes("last") || raw.toLowerCase().includes("admin")
          ? "Cannot remove the last admin. Promote another member to admin first, then remove this role."
          : raw;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const dialogConfig = useMemo(() => {
    if (!target) return null;
    const name = fullName(target.user);
    switch (target.kind) {
      case "impersonate":
        return {
          title: `Impersonate ${name}?`,
          body: `You will be redirected to the main app as ${name}. An impersonation session will be recorded in the audit log. The session expires after 5 minutes of inactivity.`,
          confirmLabel: "Impersonate",
          confirmClass: "bg-amber-500 hover:bg-amber-600 text-slate-950",
          requireReason: false,
          reasonLabel: "",
        };
      case "suspend":
        return {
          title: `Suspend ${name}?`,
          body: `This will immediately block ${name}'s access. They will see a suspended account screen.`,
          confirmLabel: "Suspend",
          confirmClass: "bg-amber-500 hover:bg-amber-600 text-slate-950",
          requireReason: true,
          reasonLabel: "Reason for suspension",
        };
      case "reactivate":
        return {
          title: `Reactivate ${name}?`,
          body: `This will restore ${name}'s access and set their account back to active.`,
          confirmLabel: "Reactivate",
          confirmClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
          requireReason: false,
          reasonLabel: "",
        };
      case "deactivate":
        return {
          title: `Deactivate ${name}?`,
          body: `This will permanently deactivate ${name}'s account. They will need to be manually reactivated by a company admin or superuser.`,
          confirmLabel: "Deactivate",
          confirmClass: "bg-red-600 hover:bg-red-700 text-white",
          requireReason: true,
          reasonLabel: "Reason for deactivation",
        };
      case "grant_admin":
        return {
          title: `Grant admin role to ${name}?`,
          body: `${name} will be promoted to company admin and will be able to manage their team and company settings.`,
          confirmLabel: "Grant Admin",
          confirmClass: "bg-blue-600 hover:bg-blue-700 text-white",
          requireReason: false,
          reasonLabel: "",
        };
      case "remove_admin":
        return {
          title: `Remove admin role from ${name}?`,
          body: `${name} will be demoted to a regular member. If they are the last admin, this action will be blocked by the database.`,
          confirmLabel: "Remove Admin",
          confirmClass: "bg-slate-600 hover:bg-slate-700 text-white",
          requireReason: false,
          reasonLabel: "",
        };
    }
  }, [target]);

  const hasActiveFilters =
    filters.roles.length > 0 ||
    filters.companyIds.length > 0 ||
    filters.profileCompleted !== null ||
    filters.suspendedByCompany !== null ||
    filters.joinedDateRanges.length > 0;


  const renderFilterPanel = () => {
    if (!activeFilterType) return null;
    const closeBtn = (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setActiveFilterType(null)}
        className="h-7 text-slate-300 hover:bg-slate-700"
      >
        Close
      </Button>
    );

    if (activeFilterType === "role") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Role</div>
            {closeBtn}
          </div>
          <div className="flex gap-4">
            {["admin", "member"].map((r) => {
              const checked = filters.roles.includes(r);
              return (
                <label key={r} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = new Set(filters.roles);
                      if (c) next.add(r);
                      else next.delete(r);
                      updateFilters({ roles: Array.from(next) });
                    }}
                  />
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    if (activeFilterType === "company") {
      const options = allCompanies.map((c) => ({ value: c.id, label: c.name }));
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Company</div>
            {closeBtn}
          </div>
          <ComboboxFilter
            value=""
            onValueChange={(id) => {
              if (!filters.companyIds.includes(id)) {
                updateFilters({ companyIds: [...filters.companyIds, id] });
              }
            }}
            options={options}
            placeholder="Select company…"
            searchPlaceholder="Search companies…"
            emptyMessage="No companies found"
            width="w-[280px]"
          />
        </div>
      );
    }

    if (activeFilterType === "profileCompleted") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Profile complete</div>
            {closeBtn}
          </div>
          <div className="flex gap-4">
            {[true, false].map((v) => (
              <label key={String(v)} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <input
                  type="radio"
                  name="profileCompleted"
                  checked={filters.profileCompleted === v}
                  onChange={() => {
                    updateFilters({ profileCompleted: v });
                    setActiveFilterType(null);
                  }}
                />
                {v ? "Complete" : "Incomplete"}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (activeFilterType === "suspendedByCompany") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Suspended by company</div>
            {closeBtn}
          </div>
          <div className="flex gap-4">
            {[true, false].map((v) => (
              <label key={String(v)} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                <input
                  type="radio"
                  name="suspendedByCompany"
                  checked={filters.suspendedByCompany === v}
                  onChange={() => {
                    updateFilters({ suspendedByCompany: v });
                    setActiveFilterType(null);
                  }}
                />
                {v ? "Yes" : "No"}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (activeFilterType === "joinedDate") {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-slate-400">Date joined (any range matches)</div>
            {closeBtn}
          </div>
          <div className="space-y-2">
            {filters.joinedDateRanges.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="date"
                  value={r.from ?? ""}
                  onChange={(e) => updateFilters({
                    joinedDateRanges: filters.joinedDateRanges.map((x, j) => j === i ? { ...x, from: e.target.value || null } : x),
                  })}
                  className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <span className="text-xs text-slate-500">to</span>
                <Input
                  type="date"
                  value={r.to ?? ""}
                  onChange={(e) => updateFilters({
                    joinedDateRanges: filters.joinedDateRanges.map((x, j) => j === i ? { ...x, to: e.target.value || null } : x),
                  })}
                  className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => updateFilters({
                    joinedDateRanges: filters.joinedDateRanges.filter((_, j) => j !== i),
                  })}
                  className="h-9 text-slate-400 hover:text-white"
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateFilters({
                joinedDateRanges: [...filters.joinedDateRanges, { from: null, to: null }],
              })}
              className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              + Add range
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  const statusTabValue: "all" | "active" | "suspended" | "deactivated" =
    filters.status === "active"
      ? "active"
      : filters.status === "suspended"
      ? "suspended"
      : filters.status === "deactivated"
      ? "deactivated"
      : "all";

  return (
    <>
      <div className="space-y-4">
        {/* Status tabs */}
        <Tabs
          value={statusTabValue}
          onValueChange={(v) => updateFilters({ status: v === "all" ? null : v })}
        >
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="all" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              All{userCounts ? ` (${userCounts.all})` : ''}
            </TabsTrigger>
            <TabsTrigger value="active" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              Active{userCounts ? ` (${userCounts.active})` : ''}
            </TabsTrigger>
            <TabsTrigger value="suspended" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              Suspended{userCounts ? ` (${userCounts.suspended})` : ''}
            </TabsTrigger>
            <TabsTrigger value="deactivated" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
              Deactivated{userCounts ? ` (${userCounts.deactivated})` : ''}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search current page by name or email…"
              className="pl-8 h-9 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Showing {visible.length} of {total} users
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadUsersCsv(visible)}
              disabled={visible.length === 0}
              className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <Download className="h-3.5 w-3.5 mr-2" />
              Export CSV
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => refetch()}
              className="h-9 w-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading || isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Filter pill bar */}
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-9 border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
              >
                + Add filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700 text-slate-100">
              {AVAILABLE_FILTERS.map((f) => (
                <DropdownMenuItem
                  key={f.value}
                  onClick={() => setActiveFilterType(f.value as FilterType)}
                  className="focus:bg-slate-800 focus:text-white"
                >
                  {f.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {filters.roles.length > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
              onClick={() => setActiveFilterType(activeFilterType === "role" ? null : "role")}
            >
              Role: {filters.roles.join(", ")}
              <button onClick={(e) => { e.stopPropagation(); updateFilters({ roles: [] }); }} className="ml-1 text-slate-400 hover:text-white">×</button>
            </span>
          )}
          {filters.companyIds.map((id) => {
            const name = allCompanies.find((c) => c.id === id)?.name ?? id;
            return (
              <span
                key={`co-${id}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
                onClick={() => setActiveFilterType(activeFilterType === "company" ? null : "company")}
              >
                Company: {name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFilters({ companyIds: filters.companyIds.filter((x) => x !== id) });
                  }}
                  className="ml-1 text-slate-400 hover:text-white"
                >×</button>
              </span>
            );
          })}
          {filters.profileCompleted !== null && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
              onClick={() => setActiveFilterType(activeFilterType === "profileCompleted" ? null : "profileCompleted")}
            >
              Profile: {filters.profileCompleted ? "Complete" : "Incomplete"}
              <button onClick={(e) => { e.stopPropagation(); updateFilters({ profileCompleted: null }); }} className="ml-1 text-slate-400 hover:text-white">×</button>
            </span>
          )}
          {filters.suspendedByCompany !== null && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
              onClick={() => setActiveFilterType(activeFilterType === "suspendedByCompany" ? null : "suspendedByCompany")}
            >
              Suspended by company: {filters.suspendedByCompany ? "Yes" : "No"}
              <button onClick={(e) => { e.stopPropagation(); updateFilters({ suspendedByCompany: null }); }} className="ml-1 text-slate-400 hover:text-white">×</button>
            </span>
          )}
          {filters.joinedDateRanges.map((r, i) => {
            const label = r.from && r.to
              ? `Joined: ${r.from} to ${r.to}`
              : r.from
              ? `Joined: from ${r.from}`
              : r.to
              ? `Joined: until ${r.to}`
              : `Joined: any`;
            return (
              <span
                key={`jd-${i}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600 cursor-pointer hover:bg-slate-600"
                onClick={() => setActiveFilterType(activeFilterType === "joinedDate" ? null : "joinedDate")}
              >
                {label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateFilters({ joinedDateRanges: filters.joinedDateRanges.filter((_, j) => j !== i) });
                  }}
                  className="ml-1 text-slate-400 hover:text-white"
                >×</button>
              </span>
            );
          })}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-white underline">
              Clear all
            </button>
          )}
        </div>

        {renderFilterPanel()}

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-400 uppercase text-xs">
              <tr>
                <th className="text-left font-medium px-3 py-2">Name</th>
                <th className="text-left font-medium px-3 py-2">Email</th>
                <th className="text-left font-medium px-3 py-2">Company</th>
                <th className="text-left font-medium px-3 py-2">Role</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-right font-medium px-3 py-2 w-12">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-800">
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-32 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-48 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-28 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-16 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-20 bg-slate-800" /></td>
                      <td className="px-3 py-2.5"><Skeleton className="h-4 w-6 ml-auto bg-slate-800" /></td>
                    </tr>
                  ))}
                </>
              )}

              {!isLoading && error && (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center">
                    <p className="text-sm text-red-400 mb-3">{error}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetch()}
                      className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-2" />
                      Retry
                    </Button>
                  </td>
                </tr>
              )}

              {!isLoading && !error && visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center text-sm text-slate-400">
                    No users match your filters.
                  </td>
                </tr>
              )}

              {!isLoading && !error && visible.map((u) => {
                const role = (u.admin_role ?? "").toLowerCase();
                const status = (u.status ?? "").toLowerCase();
                const isSuperuser = role === "superuser";
                const canImpersonate = status === "active" && !isSuperuser;
                const canSuspend = status === "active";
                const canReactivate = status === "suspended";
                const canDeactivate = status === "active" || status === "suspended";
                const canReactivateDeactivated = status === "deactivated";
                const canGrantAdmin = Boolean(u.company_id) && (u.admin_role ?? "").toLowerCase() !== "admin" && !isSuperuser && status === "active";
                const canRemoveAdmin = Boolean(u.company_id) && (u.admin_role ?? "").toLowerCase() === "admin";
                const hasAnyAction = canImpersonate || canSuspend || canReactivate || canDeactivate || canReactivateDeactivated || canGrantAdmin || canRemoveAdmin;

                return (
                  <tr
                    key={u.id}
                    onClick={() => setProfileTarget(u)}
                    className="border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{fullName(u)}</span>
                        {!isProfileComplete(u) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-700/40 text-slate-400 border border-slate-600/40">
                            Incomplete
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-400">{u.email || "—"}</td>
                    <td className="px-3 py-2.5">
                      {u.company_name ? (
                        <span className="text-slate-300">{u.company_name}</span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5"><RoleBadge role={u.admin_role} /></td>
                    <td className="px-3 py-2.5"><StatusBadge status={u.status} /></td>
                    <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      {hasAnyAction && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-700"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-slate-900 border-slate-700 text-slate-100"
                          >
                            {canImpersonate && (
                              <DropdownMenuItem
                                onClick={() => openAction("impersonate", u)}
                                className="focus:bg-slate-800 focus:text-white"
                              >
                                Impersonate
                              </DropdownMenuItem>
                            )}
                            {canSuspend && (
                              <DropdownMenuItem
                                onClick={() => openAction("suspend", u)}
                                className="text-amber-400 focus:bg-slate-800 focus:text-amber-300"
                              >
                                Suspend User
                              </DropdownMenuItem>
                            )}
                            {canReactivate && (
                              <DropdownMenuItem
                                onClick={() => openAction("reactivate", u)}
                                className="text-emerald-400 focus:bg-slate-800 focus:text-emerald-300"
                              >
                                Reactivate User
                              </DropdownMenuItem>
                            )}
                            {canReactivateDeactivated && (
                              <DropdownMenuItem
                                onClick={() => openAction("reactivate", u)}
                                className="text-emerald-400 focus:bg-slate-800 focus:text-emerald-300"
                              >
                                Reactivate User
                              </DropdownMenuItem>
                            )}
                            {canDeactivate && (
                              <DropdownMenuItem
                                onClick={() => openAction("deactivate", u)}
                                className="text-red-400 focus:bg-slate-800 focus:text-red-300"
                              >
                                Deactivate User
                              </DropdownMenuItem>
                            )}
                            {canGrantAdmin && (
                              <DropdownMenuItem
                                onClick={() => openAction("grant_admin", u)}
                                className="text-blue-400 focus:bg-slate-800 focus:text-blue-300"
                              >
                                Make Admin
                              </DropdownMenuItem>
                            )}
                            {canRemoveAdmin && (
                              <DropdownMenuItem
                                onClick={() => openAction("remove_admin", u)}
                                className="text-slate-400 focus:bg-slate-800 focus:text-slate-300"
                              >
                                Remove Admin
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && !error && total > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{total} total users</span>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                disabled={filters.page <= 1}
                onClick={() => updateFilters({ page: filters.page - 1 })}
                className="h-7 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Previous
              </Button>
              <span className="text-xs text-slate-400">
                Page {filters.page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={filters.page >= totalPages}
                onClick={() => updateFilters({ page: filters.page + 1 })}
                className="h-7 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <UserProfileSheet
        user={profileTarget}
        open={!!profileTarget}
        onOpenChange={(open) => { if (!open) setProfileTarget(null); }}
      />

      {/* Confirmation dialog */}
      <Dialog open={!!target} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          {dialogConfig && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">{dialogConfig.title}</DialogTitle>
                <DialogDescription className="text-slate-400">
                  {dialogConfig.body}
                </DialogDescription>
              </DialogHeader>
              {target?.kind === 'suspend' ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Suspension type</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                        <input
                          type="radio"
                          name="suspensionType"
                          value="temporary"
                          checked={suspensionType === 'temporary'}
                          onChange={() => setSuspensionType('temporary')}
                        />
                        Temporary
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                        <input
                          type="radio"
                          name="suspensionType"
                          value="permanent"
                          checked={suspensionType === 'permanent'}
                          onChange={() => setSuspensionType('permanent')}
                        />
                        Permanent
                      </label>
                    </div>
                  </div>
                  {suspensionType === 'temporary' && (
                    <div className="space-y-2">
                      <Label htmlFor="suspensionDays" className="text-slate-300">
                        Number of days <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="suspensionDays"
                        type="number"
                        min="1"
                        max="365"
                        value={suspensionDays}
                        onChange={(e) => setSuspensionDays(e.target.value)}
                        className="bg-slate-950 border-slate-700 text-slate-100"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="reason" className="text-slate-300">
                      Reason for suspension <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="bg-slate-950 border-slate-700 text-slate-100"
                      autoFocus
                    />
                  </div>
                </div>
              ) : dialogConfig.requireReason && (
                <div className="space-y-2">
                  <Label htmlFor="reason" className="text-slate-300">
                    {dialogConfig.reasonLabel} <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-100"
                    autoFocus
                  />
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={closeDialog}
                  disabled={submitting}
                  className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={
                    submitting ||
                    (target?.kind === 'suspend'
                      ? !reason.trim() || (suspensionType === 'temporary' && (!suspensionDays || parseInt(suspensionDays) < 1 || parseInt(suspensionDays) > 365))
                      : dialogConfig.requireReason && !reason.trim())
                  }
                  className={dialogConfig.confirmClass}
                >
                  {submitting ? "Working…" : dialogConfig.confirmLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
